/* ============================================================
   MY AGENT — LAKARAN A: SENARAI + STEPPER BERPANDU  (PROTOTAIP)
   ============================================================
   Dipapar pada /prostudio-v3.9/agent SAHAJA.

   ⛔ TIADA LOGIK SEBENAR. Tiada Firestore, tiada jambatan extension,
      tiada AI. Semua data contoh dan hilang bila halaman dimuat semula.
      Larian "Configure" di Langkah 5 ialah lakonan bermasa — ia TIDAK
      membuka ChatGPT dan TIDAK menghantar apa-apa produk.

      Seksyen yang SEBENAR kekal di ProStudioAgentSection.jsx dan itulah
      yang dipakai pada halaman hidup /prostudio/agent.

   ── TIGA TINGKAT (bukan dua) ───────────────────────────────────
     1  Senarai agent   — setiap baris bawa statusnya sendiri
     2  Stepper (agent baharu)  ATAU  ringkasan (agent siap)
     3  Edit satu langkah sahaja

   Tingkat 1 ditambah 16 Ogos selepas user bertanya "di mana senarai
   agent yang sudah disimpan?". Versi pertama lakaran ini terus masuk
   ke wizard, jadi pelanggan yang sudah ada tiga agent tiada tempat
   untuk melihatnya. Seksyen sebenar memang ada senarai itu (rel kiri),
   jadi lakaran tanpa senarai membandingkan dua benda yang tidak sama.

   ── Kenapa stepper, bukan satu borang panjang ──────────────────
   Lima langkah ini BERSANDAR antara satu sama lain: borang di Langkah 4
   dibina mengikut jawapan Langkah 3 (berapa set, berapa babak, gambar
   sahaja atau gambar + video). Borang panjang membenarkan pelanggan
   mengisi Langkah 4 dahulu — dan borang itu belum tahu bentuknya.

   ⚠️ Status "belum Configure" BUKAN hiasan: dalam Workspace sebenar,
      agent begitu dikelabukan dalam pemilih agent dan tidak boleh
      dijalankan. Sebab itu ia dipapar pada baris senarai.
   ============================================================ */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bot, Check, ArrowLeft, ArrowRight, Lock, Plus,
    Image as IkonGambar, Video, Type, Pencil, ChevronRight,
} from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, EmptyState } from '../design/ui';
import { Lbl, Inp, Ta, Sel, Cip, Nota, Kad } from './prostudioUI';
import SenaraiAgentV39 from './SenaraiAgentV39';
import ProStudioKonfigurV39 from './ProStudioKonfigurV39';
import {
    PRODUK_UJIAN, SUMBER_CONTOH, NILAI_BLOK, NILAI_SET, JAWAPAN_CONTOH,
    TAJUK_CONTOH, LABEL_CONTOH, medanAgent, dataKosong, agentContoh,
} from './prostudioAgents';
import { slugifySource, collectFromText, cariPenanda } from '../utils/agentRecipe';
import { useT } from '../i18n';

/* ⚠️ Lakaran ini ada LIMA langkah; seksyen SEBENAR ada empat (ia tidak
   memisahkan "Medan" daripada "Hasil"). Langkah keempat di sini dulu
   meminjam kunci `s4` — kunci milik langkah Configure — jadi DUA langkah
   berturut-turut bertajuk "Configure" dan bertanya soalan yang sama
   (dilihat pada skrin 2026-08-19). Langkah medan kini ada kuncinya
   sendiri; `s4`/`s5` kekal milik langkah Configure. */
const LANGKAH = [
    { tajuk: 'prostudio.wizard.s1.name', soalan: 'prostudio.wizard.s1.q' },
    { tajuk: 'prostudio.wizard.s2.name', soalan: 'prostudio.wizard.s2.q' },
    { tajuk: 'prostudio.wizard.s3.name', soalan: 'prostudio.wizard.s3.q' },
    { tajuk: 'prostudio.cfg.fieldsName', soalan: 'prostudio.cfg.fieldsQ' },
    { tajuk: 'prostudio.wizard.s5.name', soalan: 'prostudio.wizard.s5.q' },
];

/* Keadaan borang bagi satu alamat.

   Dipakai DUA kali — sekali semasa mount, sekali oleh penyelaras render
   bila alamat bertukar. Satu fungsi, supaya kedua-duanya tidak boleh
   terpesong: versi pertama hanya menyelaras pada PERUBAHAN alamat, jadi
   membuka /agent/<id> terus daripada bar alamat memaparkan wizard kosong
   walaupun agent itu sudah siap. */
function keadaanBagi(agentId, senarai) {
    if (agentId && agentId !== 'baharu') {
        const a = senarai.find((x) => x.id === agentId);
        if (a) {
            return {
                d: { ...a.data },
                langkah: a.configured ? 5 : 0,
                larian: a.configured ? 'siap' : 'idle',
                baris: a.configured ? 3 : 0,
            };
        }
    }
    return { d: dataKosong(), langkah: 0, larian: 'idle', baris: 0 };
}

/* Ikon setiap medan datang sebagai NAMA (teks) daripada prostudioAgents,
   bukan sebagai komponen — supaya senarai medan itu kekal data tulen dan
   tidak terikat kepada set ikon mana-mana fail. */
const IKON_MEDAN = { gambar: IkonGambar, video: Video, teks: Type };

/* Tukar templat kepada ayat sebenar untuk pratonton: setiap blok @xxx
   digantikan dengan nilai contohnya bagi SET itu. Blok yang tidak dikenali
   dibiarkan seperti asal — pelanggan patut nampak yang ia tidak menjadi
   apa-apa. */
const resolusiBlok = (templat, nilai = NILAI_BLOK) => String(templat || '')
    .replace(/@([a-zA-Z0-9_]+)/g, (asal, slug) => {
        const v = nilai[String(slug).toLowerCase()];
        return v === undefined ? asal : v;
    })
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/* `agentId` datang daripada ALAMAT, bukan keadaan dalaman:
     tiada       → senarai penuh
     'baharu'    → wizard kosong
     id agent    → wizard atau ringkasan agent itu
   Setiap agent ada alamatnya sendiri, jadi ia boleh di-bookmark dan
   butang Back browser berfungsi — sama seperti seksyen PRO Studio. */
export default function ProStudioAgentStepperV39({ agentId, asas = '/prostudio-v3.9/agent' }) {
    const t = useT();
    const navigate = useNavigate();

    const [agents, setAgents] = useState(agentContoh);
    /* Keadaan awal dikira daripada ALAMAT, bukan dipaku kosong — buka
       /agent/<id> terus daripada bar alamat mesti memaparkan agent itu. */
    const [mula] = useState(() => keadaanBagi(agentId, agentContoh()));
    const [langkah, setLangkah] = useState(mula.langkah);   // 0–4 = langkah, 5 = ringkasan
    const [d, setD] = useState(mula.d);
    const [ralat, setRalat] = useState('');
    const [larian, setLarian] = useState(mula.larian);      // idle | jalan | siap
    const [barisLarian, setBarisLarian] = useState(mula.baris);
    /* Lakonan "baca senarai model" — pada halaman hidup ini membuka Gem sebenar
       dan menyalin senarainya. Di sini ia menunggu sekejap kemudian mengisi
       senarai contoh, supaya aliran "pilih dahulu, baru Configure" boleh dinilai. */
    const [bacaModel, setBacaModel] = useState(false);
    /* Lakonan "padan semula" bila pusingan jawapan ditukar — pada halaman hidup
       ia memanggil pemadan AI (berpuluh saat). Di sini ia menunggu sekejap,
       supaya tanda sibuk pada skrin hasil boleh dinilai tanpa agent sebenar. */
    const [pusinganPilih, setPusinganPilih] = useState(0);
    const [padanSibuk, setPadanSibuk] = useState(false);
    /* Blok buatan sendiri — pada halaman hidup ia disimpan pada agent; di sini
       ia hidup dalam ingatan sahaja supaya aliran boleh dinilai. */
    const [blokSendiri, setBlokSendiri] = useState([]);
    /* Kod blok → teks blok. Blok tetap mengekalkan kod yang templat medan
       memang guna; blok buatan sendiri ditambah selepasnya dan dinomborkan
       kalau kodnya bertembung — peraturan yang SAMA seperti petaSlug() pada
       halaman hidup. */
    const blokPetaContoh = { ...LABEL_CONTOH };
    blokSendiri.forEach((teks) => {
        const asas = slugifySource(teks);
        if (!asas) return;
        let kod = asas;
        let n = 2;
        while (blokPetaContoh[kod] !== undefined) { kod = asas + n; n += 1; }
        blokPetaContoh[kod] = teks;
    });
    const pemasa = useRef([]);

    /* Pemasa lakonan WAJIB dibersihkan — keluar dari halaman semasa
       larian berjalan meninggalkan setState pada komponen yang tiada. */
    useEffect(() => () => pemasa.current.forEach(clearTimeout), []);

    const tukar = (tampalan) => setD((p) => ({ ...p, ...tampalan }));
    const aktif = agentId || null;

    /* Selaraskan borang dengan alamat SEMASA RENDER, bukan dalam effect.

       Ini corak rasmi React untuk "laraskan keadaan bila prop berubah"
       (react.dev — You Might Not Need an Effect). Melakukannya dalam
       effect menyebabkan render bertingkat, dan lint projek ini memang
       melarangnya.

       Perbandingan `idDimuat !== agentId` yang menjaga borang: tanpa ia,
       setiap kemas kini pada `agents` (contohnya menyimpan agent) akan
       memuat semula borang di tengah jalan dan membuang apa yang sedang
       ditaip. */
    const [idDimuat, setIdDimuat] = useState(agentId);
    if (idDimuat !== agentId) {
        setIdDimuat(agentId);
        setRalat('');
        const k = keadaanBagi(agentId, agents);
        setD(k.d); setLangkah(k.langkah); setLarian(k.larian); setBarisLarian(k.baris);
    }

    /* Alamat agent yang tidak wujud → pulangkan ke senarai. Ia effect
       (bukan render) kerana navigate ialah kesan sampingan. */
    useEffect(() => {
        if (agentId && agentId !== 'baharu' && !agents.some((a) => a.id === agentId)) {
            navigate(asas, { replace: true });
        }
    }, [agentId, agents, navigate, asas]);

    /* Larian lakonan agent lain tidak boleh menyambung ke agent ini. */
    useEffect(() => { pemasa.current.forEach(clearTimeout); }, [agentId]);

    /* ── Tingkat 1 → 2 ──────────────────────────────────────────── */
    const bukaAgent = (a) => navigate(`${asas}/${a.id}`);
    const agentBaharu = () => navigate(`${asas}/baharu`);
    const kembaliKeSenarai = () => navigate(asas);

    const padamAgent = (id) => setAgents((p) => p.filter((a) => a.id !== id));

    /* ── Syarat setiap langkah ──────────────────────────────────── */
    const masalah = (i) => {
        if (i === 0) {
            if (!d.nama.trim()) return t('prostudio.wizard.errName');
            if (!d.url.trim()) return t('prostudio.wizard.errUrlEmpty');
            /* Semakan ikut PLATFORM. Sebelum ini ia sentiasa menuntut pautan
               chatgpt.com — jadi lakaran menawarkan cip "Gemini" kemudian
               menolak pautan Gemini yang ditampal (dilihat 2026-08-20).
               Seksyen sebenar tidak menyemak bentuk pautan langsung; di sini ia
               dikekalkan kerana lakaran memang alat untuk menilai aliran. */
            if (d.platform === 'gemini') {
                if (!/gemini\.google\.com\/gem\//.test(d.url)) return t('prostudio.cfg.errGemUrl');
            } else if (!/chatgpt\.com\/g\//.test(d.url)) {
                return t('prostudio.wizard.errUrlBad');
            }
        }
        if (i === 4) {
            if (!d.produkUjian) return t('prostudio.wizard.errProduct');
            if (larian !== 'siap') return t('prostudio.wizard.errNotRun');
        }
        return '';
    };

    /* Simpan ke senarai berlaku pada hujung Langkah 5 — di situlah agent
       jadi "configured", sama seperti seksyen sebenar. */
    const simpanKeSenarai = () => {
        const rekod = { configured: true, tarikh: '16 Ogos 2026', data: { ...d } };
        if (aktif !== 'baharu' && agents.some((a) => a.id === aktif)) {
            setAgents((p) => p.map((a) => (a.id === aktif ? { ...a, ...rekod } : a)));
            return;
        }
        /* Agent baharu dapat alamatnya sendiri sebaik ia disimpan —
           kalau tidak, muat semula halaman di /baharu memulakan wizard
           kosong semula walaupun agent itu sudah ada dalam senarai. */
        const id = `a_${agents.length + 1}_${agents.length}`;
        setAgents((p) => [...p, { id, ...rekod }]);
        setIdDimuat(id);          // elak penyelaras render memuat semula borang
        navigate(`${asas}/${id}`, { replace: true });
    };

    const kehadapan = () => {
        const m = masalah(langkah);
        if (m) { setRalat(m); return; }
        setRalat('');
        if (langkah === 4) simpanKeSenarai();
        setLangkah((p) => p + 1);
    };

    const kebelakang = () => { setRalat(''); setLangkah((p) => Math.max(0, p - 1)); };

    /* Melompat daripada bar kemajuan hanya ke BELAKANG — ke hadapan
       akan memintas syarat langkah semasa. */
    const lompat = (i) => { if (i < langkah) { setRalat(''); setLangkah(i); } };

    const jalankanConfigure = () => {
        if (!d.produkUjian) { setRalat(t('prostudio.wizard.errProduct')); return; }
        setRalat('');
        setLarian('jalan');
        setBarisLarian(0);
        pemasa.current.forEach(clearTimeout);
        pemasa.current = [
            setTimeout(() => setBarisLarian(1), 900),
            setTimeout(() => setBarisLarian(2), 2100),
            setTimeout(() => {
                setBarisLarian(3);
                setLarian('siap');
                /* Lakonan "padanan dikesan": medan yang KOSONG diisi dengan
                   padanan lalai. Nilai yang pelanggan sudah tulis sendiri
                   TIDAK ditimpa — larian semula tidak boleh memadam kerja
                   tangan, itu peraturan yang sama pada seksyen sebenar. */
                setD((p) => {
                    const ada = Object.entries(p.padanan || {}).filter(([, v]) => String(v || '').trim());
                    return { ...p, padanan: { ...dataKosong().padanan, ...Object.fromEntries(ada) } };
                });
            }, 3400),
        ];
    };

    const tukarPadanan = (id, nilai) => setD((p) => ({ ...p, padanan: { ...(p.padanan || {}), [id]: nilai } }));

    /* ⛔ TIADA tambah/buang/namakan semula medan lagi. Lima medan itu TETAP
       dalam produk sebenar (ia sepadan dengan lima kotak kad produk), jadi
       kawalan itu ialah kawalan yang tidak boleh melakukan apa-apa. */

    /* ============================================================
       TINGKAT 1 — SENARAI AGENT
       ============================================================ */
    if (!aktif) {
        const adaBelumSiap = agents.some((a) => !a.configured);
        return (
            <div style={{ display: 'grid', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <Badge tone="neutral">{t('prostudio.hub.badge.agents', { n: agents.length })}</Badge>
                    <span className="s-dim" style={{ flex: 1 }}>{t('prostudio.wizard.listDesc')}</span>
                    <Btn variant="ghost" size="sm"
                        icon={<Plus size={14} strokeWidth={2} />} onClick={agentBaharu}>
                        {t('prostudio.wizard.add')}
                    </Btn>
                </div>

                {agents.length === 0 ? (
                    <EmptyState
                        title={t('prostudio.wizard.emptyTitle')}
                        action={<Btn variant="ghost" icon={<Plus size={14} strokeWidth={2} />} onClick={agentBaharu}>
                            {t('prostudio.wizard.add')}
                        </Btn>}
                    >
                        {t('prostudio.wizard.emptyBody')}
                    </EmptyState>
                ) : (
                    /* Baris senarai dibina oleh SenaraiAgentV39 — komponen
                       yang SAMA dengan yang dipakai dalam panel SMART pada
                       hub. Dua tempat, satu takrifan. */
                    <SenaraiAgentV39 agents={agents} onBuka={bukaAgent} padam={padamAgent} />
                )}

                {adaBelumSiap && <Nota jenis="warn">{t('prostudio.wizard.cannotRun')}</Nota>}
            </div>
        );
    }

    /* ============================================================
       TINGKAT 2 — RINGKASAN (agent yang sudah siap)
       ============================================================ */
    const barisRingkasan = [
        { label: t('prostudio.wizard.sumAgent'), nilai: d.nama || '—', ke: 0 },
        { label: t('prostudio.wizard.sumPlatform'), nilai: 'ChatGPT', ke: 0 },
        { label: t('prostudio.wizard.sumReplies'), nilai: `${d.balasan}×`, ke: 1 },
        { label: t('prostudio.wizard.sumSets'), nilai: t('prostudio.wizard.sumSetsValue', { set: d.set, babak: d.babak }), ke: 2 },
        {
            label: t('prostudio.wizard.sumOutput'),
            nilai: d.hasil === 'gambarvideo' ? t('prostudio.wizard.outImageVideo') : t('prostudio.wizard.outImageOnly'),
            ke: 2,
        },
        {
            label: t('prostudio.wizard.sumFields'),
            nilai: t('prostudio.wizard.sumFieldsValue', {
                n: medanAgent(d).length,
            }),
            ke: 3,
        },
        { label: t('prostudio.wizard.sumTestProduct'), nilai: d.produkUjian || '—', ke: 4 },
    ];

    const pautanSenarai = (
        <button
            type="button"
            onClick={kembaliKeSenarai}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 14,
                padding: 0, background: 'none', border: 0, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: 500, color: 'var(--s-ink-3)',
            }}
        >
            <ArrowLeft size={15} strokeWidth={1.9} /> {t('prostudio.wizard.listTitle')}
        </button>
    );

    if (langkah > 4) {
        return (
            <div style={{ display: 'grid', gap: 14 }}>
                {pautanSenarai}

                <Nota jenis="ok">
                    <strong style={{ color: 'var(--s-ink)' }}>{t('prostudio.wizard.doneTitle', { nama: d.nama })}</strong>{' '}
                    {t('prostudio.wizard.doneBody')}
                </Nota>

                {/* Selepas siap, stepper bertukar menjadi ringkasan yang boleh
                    diedit sebahagian-sebahagian. Inilah sebabnya stepper tidak
                    menyusahkan kemudian hari. */}
                <Kad tajuk={t('prostudio.wizard.sumTitle')} perihal={t('prostudio.wizard.sumDesc')}>
                    <div style={{ display: 'grid', gap: 2 }}>
                        {barisRingkasan.map((r) => (
                            <div key={r.label} style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '9px 0', borderBottom: '1px solid var(--s-line)',
                            }}>
                                <span style={{ flex: '0 0 150px', fontSize: 12.5, color: 'var(--s-ink-3)' }}>{r.label}</span>
                                <span style={{
                                    flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--s-ink)',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>{r.nilai}</span>
                                <Btn variant="quiet" size="sm" icon={<Pencil size={13} strokeWidth={2} />}
                                    onClick={() => { setRalat(''); setLangkah(r.ke); }}>
                                    {t('prostudio.wizard.edit')}
                                </Btn>
                            </div>
                        ))}
                    </div>
                </Kad>

                <EmptyState title={t('prostudio.wizard.sketchTitle')}>
                    {t('prostudio.wizard.sketchBody')}
                </EmptyState>
            </div>
        );
    }

    /* ============================================================
       TINGKAT 2 — STEPPER
       ============================================================ */
    const barKemajuan = (
        <div style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                {/* Kunci sendiri, bukan `prostudio.wizard.stepOf` — kunci itu
                    memaku "daripada 4" dan dikongsi dengan seksyen SEBENAR
                    yang memang ada 4 langkah. Lakaran ini ada LIMA, jadi ia
                    memapar "Langkah 5 daripada 4" (dilihat pada skrin
                    2026-08-19). Jumlah dikira, tidak lagi ditaip. */}
                <span style={{ fontSize: 13, color: 'var(--s-ink-2)' }}>
                    {t('prostudio.cfg.stepOf', { n: langkah + 1, jum: LANGKAH.length })}
                </span>
                <span style={{ fontSize: 13, color: 'var(--s-ink-3)' }}>· {t(LANGKAH[langkah].tajuk)}</span>
            </div>
            <div style={{ display: 'flex', gap: 5 }}>
                {LANGKAH.map((l, i) => {
                    const lepas = i < langkah;
                    const kini = i === langkah;
                    return (
                        <button
                            key={l.tajuk}
                            type="button"
                            onClick={() => lompat(i)}
                            title={t(l.tajuk)}
                            style={{
                                flex: 1, height: 5, padding: 0, borderRadius: 3, border: 0,
                                cursor: lepas ? 'pointer' : 'default',
                                background: lepas || kini ? 'var(--s-ink)' : 'var(--s-line-strong)',
                                opacity: kini ? 1 : lepas ? 0.55 : 1,
                                transition: 'background .15s ease, opacity .15s ease',
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );

    const l1 = (
        <div style={{ display: 'grid', gap: 14 }}>
            <div>
                <Lbl>{t('prostudio.wizard.agentName')}</Lbl>
                <Inp value={d.nama} placeholder={t('prostudio.wizard.agentNamePh')}
                    onChange={(e) => tukar({ nama: e.target.value })} />
                <p className="s-dim" style={{ marginTop: 6 }}>{t('prostudio.wizard.agentNameHint')}</p>
            </div>

            <div>
                <Lbl>{t('prostudio.wizard.platform')}</Lbl>
                <div style={{ display: 'flex', gap: 7 }}>
                    <Cip on={d.platform === 'chatgpt'} onClick={() => tukar({ platform: 'chatgpt' })}>ChatGPT</Cip>
                    <Cip on={d.platform === 'gemini'} onClick={() => tukar({ platform: 'gemini' })}>Gemini</Cip>
                </div>
                <p className="s-dim" style={{ marginTop: 6 }}>{t('prostudio.wizard.platformHint')}</p>
            </div>

            <div>
                <Lbl>{t('prostudio.wizard.gptUrl')}</Lbl>
                <Inp value={d.url} placeholder="https://chatgpt.com/g/g-xxxxxxxx"
                    onChange={(e) => tukar({ url: e.target.value })} />
                <p className="s-dim" style={{ marginTop: 6 }}>{t('prostudio.wizard.gptUrlHint')}</p>
            </div>
        </div>
    );

    const l2 = (
        <div style={{ display: 'grid', gap: 14 }}>
            <div>
                <Lbl>{t('prostudio.wizard.extraLine')}</Lbl>
                <Ta value={d.arahan} placeholder={t('prostudio.wizard.extraLinePh')}
                    onChange={(e) => tukar({ arahan: e.target.value })} />
                <p className="s-dim" style={{ marginTop: 6 }}>{t('prostudio.wizard.extraLineHint')}</p>
            </div>

            <div>
                <Lbl>{t('prostudio.wizard.replies')}</Lbl>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[1, 2, 3, 4, 5].map((n) => (
                        <Cip key={n} kecil on={d.balasan === n} onClick={() => tukar({ balasan: n })}>{n}</Cip>
                    ))}
                </div>
                <p className="s-dim" style={{ marginTop: 6 }}>{t('prostudio.wizard.repliesHint')}</p>
            </div>
        </div>
    );

    const l3 = (
        <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 }}>
                <div>
                    <Lbl>{t('prostudio.wizard.setsPerProduct')}</Lbl>
                    <div style={{ display: 'flex', gap: 6 }}>
                        {[1, 2, 3].map((n) => (
                            <Cip key={n} kecil on={d.set === n} onClick={() => tukar({ set: n })}>{n}</Cip>
                        ))}
                    </div>
                </div>
                <div>
                    <Lbl>{t('prostudio.wizard.scenesPerSet')}</Lbl>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {[1, 2, 3, 4, 5].map((n) => (
                            <Cip key={n} kecil on={d.babak === n} onClick={() => tukar({ babak: n })}>{n}</Cip>
                        ))}
                    </div>
                </div>
            </div>

            <div>
                <Lbl>{t('prostudio.wizard.finalOutput')}</Lbl>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
                    {[
                        { id: 'gambarvideo', tajuk: 'prostudio.wizard.outImageVideo', sub: 'prostudio.wizard.outImageVideoSub' },
                        { id: 'gambar', tajuk: 'prostudio.wizard.outImageOnly', sub: 'prostudio.wizard.outImageOnlySub' },
                    ].map((o) => {
                        const on = d.hasil === o.id;
                        return (
                            <button
                                key={o.id}
                                type="button"
                                onClick={() => tukar({ hasil: o.id })}
                                style={{
                                    textAlign: 'left', padding: '11px 13px', borderRadius: 10, cursor: 'pointer',
                                    background: on ? 'var(--s-fill)' : 'transparent',
                                    border: `1px solid ${on ? 'var(--s-ink-3)' : 'var(--s-line-strong)'}`,
                                    fontFamily: 'inherit',
                                }}
                            >
                                <span style={{
                                    display: 'flex', alignItems: 'center', gap: 7,
                                    fontSize: 13.5, fontWeight: 500, color: 'var(--s-ink)', marginBottom: 3,
                                }}>
                                    {on && <Check size={14} strokeWidth={2.2} />}{t(o.tajuk)}
                                </span>
                                <span style={{ fontSize: 12, color: 'var(--s-ink-3)' }}>{t(o.sub)}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 }}>
                <div>
                    <Lbl>{t('prostudio.wizard.productPhoto')}</Lbl>
                    <Sel value={d.gambarProduk} onChange={(e) => tukar({ gambarProduk: e.target.value })}>
                        <option value="setiap">{t('prostudio.wizard.photoEvery')}</option>
                        <option value="akhir">{t('prostudio.wizard.photoLast')}</option>
                        <option value="tiada">{t('prostudio.wizard.photoNone')}</option>
                    </Sel>
                </div>
                <div>
                    <Lbl>{t('prostudio.wizard.avatar')}</Lbl>
                    <Sel value={d.avatar} onChange={(e) => tukar({ avatar: e.target.value })}>
                        <option value="tiada">{t('prostudio.wizard.avatarNone')}</option>
                        <option value="guna">{t('prostudio.wizard.avatarUse')}</option>
                    </Sel>
                </div>
            </div>

            {/* Ayat hidup — setiap tekanan mengemas kininya, supaya pelanggan
                nampak akibat pilihannya tanpa perlu membayangkannya. */}
            <Nota>
                {t('prostudio.wizard.sumSentence', {
                    set: d.set,
                    babak: d.babak,
                    hasil: d.hasil === 'gambarvideo'
                        ? t('prostudio.wizard.sumSentenceVideo', { babak: d.babak })
                        : t('prostudio.wizard.sumSentenceImage'),
                    gambar: d.gambarProduk === 'setiap' ? t('prostudio.wizard.photoEvery')
                        : d.gambarProduk === 'akhir' ? t('prostudio.wizard.photoLast')
                            : t('prostudio.wizard.photoNone'),
                    avatar: d.avatar === 'guna' ? t('prostudio.wizard.avatarUse') : t('prostudio.wizard.avatarNone'),
                })}
            </Nota>
        </div>
    );

    /* SATU senarai, turutan kad produk. Kedua-dua kumpulan lama
       ("Setiap babak" / "Sekali setiap set") dibuang — lihat nota
       SEMUA_MEDAN dalam prostudioAgents. */
    const l4 = (
        <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gap: 8 }}>
                {medanAgent(d).map((f) => {
                    const Ikon = IKON_MEDAN[f.ikon] || Type;
                    return (
                        <div key={f.id} style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                            borderRadius: 8, background: 'var(--s-fill)', border: '1px solid var(--s-line)',
                        }}>
                            <Ikon size={15} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)', flex: 'none' }} />
                            <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--s-ink)' }}>{t(f.kunci)}</span>
                            {f.tanda && <Badge tone="neutral">{t(f.tanda)}</Badge>}
                            <Lock size={14} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)', flex: 'none' }} />
                        </div>
                    );
                })}
            </div>
            <p className="s-dim">{t('prostudio.cfg.fieldsHint')}</p>
        </div>
    );

    /* ⛔ SATU kumpulan, TANPA tajuk. Pemisahan "Setiap babak" / "Sekali
       setiap set" ditolak user 2026-08-19 ("tak perlu, keliru") — lima medan
       itu memang duduk bersama pada kad produk, jadi ia dipapar bersama di
       sini juga, dalam turutan kad yang sama. */
    const kumpulanPadanan = [{
        id: 'semua',
        tajuk: '',
        medan: medanAgent(d).map((f) => ({ id: f.id, nama: t(f.kunci), ikon: f.ikon })),
    }];

    /* Pratonton = satu LAJUR setiap set, sama seperti kad produk Workspace.
       Nilai contoh berbeza bagi setiap set supaya lakaran menunjukkan perkara
       yang paling mudah disalah faham: satu produk menghasilkan beberapa versi
       yang BERLAINAN, bukan satu ayat yang sama diulang. */
    const setPratonton = Array.from({ length: Math.max(1, d.set) }, (_, i) => ({
        id: `set${i + 1}`,
        tajuk: t('prostudio.cfg.previewSet', { n: i + 1 }),
        baris: medanAgent(d).map((f) => ({
            id: f.id,
            label: t(f.kunci),
            warna: f.warna,
            /* Kotak tinggi untuk medan enjin — sama pembahagian seperti kad
               sebenar (prompt panjang h-56, medan teks h-20/h-24). */
            panjang: !!f.enjin,
            nilai: resolusiBlok(d.padanan?.[f.id], NILAI_SET[i % NILAI_SET.length]),
        })),
    }));

    const l5 = (
        <ProStudioKonfigurV39
            t={t}
            larian={larian} barisLarian={barisLarian} onJalan={jalankanConfigure}
            /* Lakaran memakai nama produk sebagai id sekali gus; halaman hidup
               memakai id doc Firestore, jadi komponen menerima {id, nama}. */
            produkSenarai={PRODUK_UJIAN.map((p) => ({ id: p, nama: p }))}
            produkUjian={d.produkUjian}
            produkNama={d.produkUjian}
            onProduk={(v) => { tukar({ produkUjian: v }); setLarian('idle'); }}
            set={d.set} babak={d.babak}
            tarikh={agents.find((a) => a.id === aktif)?.tarikh || ''}
            kumpulan={kumpulanPadanan}
            sumber={Object.keys(blokPetaContoh)}
            sumberLabel={blokPetaContoh}
            padanan={d.padanan || {}}
            onPadanan={tukarPadanan}
            platform={d.platform}
            modelGemini={d.modelGemini || ''}
            onModelGemini={(v) => tukar({ modelGemini: v })}
            modelSenarai={d.geminiModels || []}
            bacaModelSibuk={bacaModel}
            onBacaModel={() => {
                setBacaModel(true);
                const id = setTimeout(() => {
                    setBacaModel(false);
                    tukar({ geminiModels: ['3.1 Pro', '3.1 Fast', '2.5 Pro', '2.5 Flash'] });
                }, 1600);
                pemasa.current.push(id);
            }}
            /* Lakaran: satu pusingan bagi setiap balasan yang dipilih di
               Langkah 2. Halaman hidup menghantar pusingan SEBENAR daripada
               larian terakhir. */
            pusingan={Array.from({ length: d.balasan }, (_, i) => ({
                nilai: i,
                label: i === 0 ? t('prostudio.agentsec.turnFirst') : t('prostudio.agentsec.turnAfter', { n: i }),
            }))}
            pusinganPilih={pusinganPilih}
            pusinganSibuk={padanSibuk}
            onPusingan={(i) => {
                setPadanSibuk(true);
                const id = setTimeout(() => { setPusinganPilih(i); setPadanSibuk(false); }, 6000);
                pemasa.current.push(id);
            }}
            jawapanPenuh={JAWAPAN_CONTOH}
            blokSendiri={blokSendiri.map((teks) => {
                const semua = collectFromText(JAWAPAN_CONTOH, [...TAJUK_CONTOH, ...blokSendiri], blokSendiri);
                const senarai = semua[teks] || [];
                return { teks, slug: Object.keys(blokPetaContoh).find((k) => blokPetaContoh[k] === teks) || slugifySource(teks), kira: senarai.length, nilai: (senarai[0] || '').slice(0, 160) };
            })}
            /* Pulangkan SEBAB, sama seperti halaman hidup — kalau tidak, lakaran
               sentiasa memapar "tiada teks diserlah" walaupun ia berjaya. */
            onTambahBlok={(teks) => {
                const t2 = String(teks || '').replace(/\s+/g, ' ').trim();
                if (!t2) return 'kosong';
                if (t2.length < 2) return 'pendek';
                if (t2.length > 120) return 'panjang';
                if (blokSendiri.includes(t2)) return 'sudah';
                /* Peraturan sama seperti halaman hidup — lihat tambahBlok dalam
                   ProStudioAgentSection: semakan PEKA huruf besar/kecil, dan blok
                   yang DIKESAN dibezakan daripada blok sendiri. Kod unik diuruskan
                   oleh petaSlug(), jadi label yang hampir serupa TIDAK ditolak. */
                if (TAJUK_CONTOH.includes(t2)) return 'dikesan';
                setBlokSendiri((p2) => [...p2, t2]);
                return 'ok';
            }}
            onBuangBlok={(teks) => setBlokSendiri((p2) => p2.filter((x) => x !== teks))}
            penandaBlok={cariPenanda(JAWAPAN_CONTOH, [...TAJUK_CONTOH, ...blokSendiri], blokSendiri)
                .filter((m) => blokSendiri.some((c) => c.toLowerCase() === String(m.heading).toLowerCase()))
                .map((m) => ({ pos: m.pos, len: m.len }))}
            pratonton={setPratonton}
        />
    );

    return (
        <div style={{ display: 'grid', gap: 0 }}>
            {pautanSenarai}
            {barKemajuan}

            <Kad
                tajuk={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
                        <span style={{
                            width: 22, height: 22, borderRadius: 999, flex: 'none',
                            display: 'grid', placeItems: 'center',
                            background: 'var(--s-select)', color: 'var(--s-select-ink)',
                            fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                        }}>{langkah + 1}</span>
                        {t(LANGKAH[langkah].soalan)}
                    </span>
                }
                perihal={t(LANGKAH[langkah].tajuk)}
                kanan={langkah === 0 ? <Bot size={17} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)' }} /> : null}
                kaki={
                    <>
                        {langkah > 0 ? (
                            <Btn variant="quiet" icon={<ArrowLeft size={14} strokeWidth={2} />} onClick={kebelakang}>
                                {t('prostudio.wizard.back')}
                            </Btn>
                        ) : <span />}

                        {/* Ralat duduk DALAM jalur kaki, bersebelahan butang yang
                            ditekan — bukan di atas skrin. */}
                        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--s-stop)' }}>{ralat}</span>

                        <Btn icon={langkah === 4 ? <Check size={14} strokeWidth={2} /> : null} onClick={kehadapan}>
                            {langkah === 4 ? t('prostudio.wizard.finish') : t('prostudio.wizard.next')}
                            {langkah < 4 && <ArrowRight size={14} strokeWidth={2} />}
                        </Btn>
                    </>
                }
            >
                {[l1, l2, l3, l4, l5][langkah]}
            </Kad>
        </div>
    );
}
